import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const CART_KEY = "ls-cart-v2";
const WISH_KEY = "ls-wishlist-v2";

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => read(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => read(WISH_KEY, []));

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem(WISH_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = (product, size, color, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((x) => x.product_id === product.id && x.size === size && x.color === color);
      if (existing) {
        return prev.map((x) =>
          x === existing ? { ...x, quantity: x.quantity + qty } : x
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          look_number: product.look_number,
          image: product.main_image || (product.images && product.images[0]) || "",
          price: product.offer_price || product.original_price || 0,
          category: product.category || null,
          subcategory: product.subcategory || null,
          customization: product.customization || null,
          delivery_time_days: product.delivery_time_days || 7,
          size,
          color: color || null,
          quantity: qty,
        },
      ];
    });
  };

  const updateQty = (index, delta) => {
    setCart((prev) => prev.map((x, i) => (i === index ? { ...x, quantity: Math.max(1, x.quantity + delta) } : x)));
  };

  const removeFromCart = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = (productId) => {
    setWishlist((prev) => (prev.includes(productId) ? prev.filter((x) => x !== productId) : [...prev, productId]));
  };

  const cartCount = cart.reduce((a, x) => a + x.quantity, 0);
  const cartTotal = cart.reduce((a, x) => a + (x.price || 0) * x.quantity, 0);

  return (
    <CartContext.Provider
      value={{ cart, wishlist, addToCart, updateQty, removeFromCart, clearCart, toggleWishlist, cartCount, cartTotal }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
